import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import { categoryService, productService, vehicleCatalogService, getDocumentUrl } from "../services/apiService";
import { useVehicle } from "../contexts/VehicleContext";
import { useTheme } from "../contexts/ThemeContext";
import { categoryPlaceholder, onImgErrorToPlaceholder } from "../utils/placeholderImage";
import { CategoryCardSkeleton } from "./Skeleton";
import CatalogueDarkPartCard from "./CatalogueDarkPartCard";
import "swiper/css";
import "swiper/css/navigation";

// Helper function to map category name to slug and image
const mapCategoryToDisplay = (categoryName) => {
  // Dynamic mapping only - no hardcoded data
  const slug = categoryName.toLowerCase().replace(/\s+/g, '_').replace(/&/g, '').replace(/\//g, '');

  return {
    title: categoryName,
    href: `/catalog/${slug}/`,
    img: categoryPlaceholder(categoryName, 80)
  };
};

export let categories = [];

// Category Card Component
const CategoryCard = React.memo(({ category, index, isGrid = false, vehicleQuery = '' }) => {
  const handleImageError = useCallback((e) => {
    onImgErrorToPlaceholder(e, 80, 80, category.title.substring(0, 2));
  }, [category.title]);

  const href = vehicleQuery ? `${category.href}${vehicleQuery}` : category.href;

  const animationProps = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay: isGrid ? index * 0.03 : index * 0.05 }
  };

  return (
    <motion.div
      {...animationProps}
      whileHover={{ y: -8 }}
      whileTap={{ scale: 0.96 }}
      className="group h-full"
    >
      <Link
        to={href}
        className="flex flex-col items-center justify-center p-5 sm:p-6 md:p-8 rounded-2xl bg-gradient-to-br from-white via-white to-red-50/30 border border-red-100/30 shadow-soft hover:shadow-medium hover:border-red-300 transition-all duration-500 w-full h-full relative overflow-hidden group/card"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 mb-4 flex items-center justify-center relative z-10">
          <img
            src={category.img}
            alt={category.title}
            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
            onError={handleImageError}
            loading="lazy"
          />
        </div>
        <span className="text-center font-bold text-gray-800 text-xs sm:text-sm md:text-base group-hover:text-red-600 transition-colors duration-300 relative z-10">
          {category.title}
        </span>
      </Link>
    </motion.div>
  );
});

CategoryCard.displayName = 'CategoryCard';

export default function SearchByCategory() {
  const swiperRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isCategoryPage = location.pathname === '/category';
  const isHomePage = location.pathname === '/';
  const { isDark } = useTheme();
  const { selectedVehicle } = useVehicle();

  /** API / UI may use `make` or `vehicleMake` (and similar); keep catalogue + heading in sync */
  const resolvedCatalogVehicle = useMemo(() => {
    if (!selectedVehicle) return null;
    const v = selectedVehicle;
    return {
      make: String(v.make || v.vehicleMake || "").trim(),
      model: String(v.model || v.vehicleModel || "").trim(),
      year: String(v.year || v.vehicleYear || "").trim(),
      variant: String(v.variant || v.vehicleVariant || "").trim(),
      fuelType: String(v.fuelType || v.vehicleFuelType || "").trim(),
      modelId: String(v.modelId || v.catalogModelId || "").trim(),
      id: v._id ?? v.id ?? null,
    };
  }, [selectedVehicle]);

  const normalizeVehiclePart = useCallback((part) => {
    const linkedProduct =
      part?.productId && typeof part.productId === "object" ? part.productId : null;
    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        const s = value.trim();
        if (!s) return [];
        if (s.startsWith("[") && s.endsWith("]")) {
          try {
            const parsed = JSON.parse(s);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return [s];
          }
        }
        if (s.includes(",")) return s.split(",").map((x) => x.trim()).filter(Boolean);
        return [s];
      }
      return value ? [value] : [];
    };
    const normalizeUrl = (v) => {
      const raw =
        typeof v === "string"
          ? v
          : (v && typeof v === "object"
              ? (v.url || v.path || v.src || v.secure_url || v.location || "")
              : "");
      const s = String(raw || "").trim().replace(/\\/g, "/");
      if (!s) return "";
      if (s === "null" || s === "undefined" || s === "[object Object]") return "";
      if (s.startsWith("http://") || s.startsWith("https://")) return s;
      return getDocumentUrl(s);
    };
    const imageCandidatesFrom = (obj) => {
      if (!obj || typeof obj !== "object") return [];
      return [
        ...toArray(obj.images),
        ...toArray(obj.imageUrls),
        ...toArray(obj.media),
        ...toArray(obj.gallery),
        ...toArray(obj.photos),
        ...toArray(obj.attachments),
        obj.imageUrl || "",
        obj.image || "",
        obj.thumbnail || "",
        obj.thumbnailUrl || "",
        obj.partImage || "",
        obj.photo || "",
        obj.icon || "",
      ];
    };
    const imageCandidates = [
      ...imageCandidatesFrom(part),
      ...imageCandidatesFrom(linkedProduct),
      ...imageCandidatesFrom(part?.product),
      ...imageCandidatesFrom(part?.details),
      ...imageCandidatesFrom(part?.productDetails),
    ]
      .map(normalizeUrl)
      .filter(Boolean);

    const partName = part.name || part.partName || part.title || "Spare Part";
    const partCategory =
      typeof part.category === "object"
        ? part.category
        : typeof part.modelCategory === "object"
          ? part.modelCategory
          : null;
    const categoryName =
      partCategory?.name ||
      part.categoryName ||
      (typeof part.category === "string" ? part.category : "") ||
      "Parts";
    const categorySlugFromApi =
      partCategory?.slug
        ? String(partCategory.slug).trim()
        : null;
    const categorySlugFromName =
      typeof categoryName === "string" && categoryName.trim()
        ? categoryName.toLowerCase().replace(/\s+/g, "_").replace(/&/g, "").replace(/\//g, "")
        : null;
    return {
      id: part._id || part.id,
      name: partName,
      brand: part.brand,
      price: part.price ?? part.sellingPrice ?? 0,
      mrp: part.mrp ?? part.listPrice ?? part.price,
      image: imageCandidates[0] || "",
      partNumber: part.partNumber || part.sku || part.oemNumber,
      category: categoryName,
      categorySlug: categorySlugFromApi || categorySlugFromName || null,
      subCategory: part.subCategory || part.subcategory,
      class: part.class || part.partClass,
      stock: part.stock ?? part.quantity ?? 10,
      origin: part.origin,
      condition: part.condition || "new",
      isOEM: part.origin === "OEM" || part.isOEM,
      reviews: part.reviews || 0,
    };
  }, []);

  // Show all categories by default on /category route
  const [showAll, setShowAll] = useState(isCategoryPage);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Build vehicle query string to append to category URLs
  const vehicleQuery = useMemo(() => {
    if (!resolvedCatalogVehicle) return '';
    const { make, model, year, variant } = resolvedCatalogVehicle;
    const params = new URLSearchParams();
    if (make) params.set('vehicleMake', make);
    if (model) params.set('vehicleModel', model);
    if (year) params.set('vehicleYear', year);
    if (variant) params.set('vehicleVariant', variant);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [resolvedCatalogVehicle]);

  // Fetch categories from backend, filtered by selected vehicle when one is active
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);

        // Build vehicle params to send to backend so it only returns categories
        // that have at least one product compatible with the selected vehicle
        const vehicleParams = resolvedCatalogVehicle
          ? {
              vehicleMake: resolvedCatalogVehicle.make,
              vehicleModel: resolvedCatalogVehicle.model,
              vehicleYear: resolvedCatalogVehicle.year,
              vehicleVariant: resolvedCatalogVehicle.variant,
              vehicleFuelType: resolvedCatalogVehicle.fuelType,
              catalogModelId: resolvedCatalogVehicle.modelId || undefined,
            }
          : {};

        const result = await categoryService.getAllActiveCategoriesFlat(vehicleParams);
        const categoriesData = result.categories || result || [];

        // Map backend categories to display format
        const mappedCategories = categoriesData
          .map(cat => {
            const mapped = mapCategoryToDisplay(cat.name || cat);
            if (cat && cat.icon) {
              mapped.img = cat.icon;
            }
            return mapped;
          })
          .filter(cat => cat && cat.title);

        setCategories(mappedCategories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [resolvedCatalogVehicle]);

  // Fetch products for the selected vehicle
  const [vehicleParts, setVehicleParts] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);

  useEffect(() => {
    if (!resolvedCatalogVehicle?.make && !resolvedCatalogVehicle?.model) {
      setVehicleParts([]);
      return;
    }
    const fetchParts = async () => {
      try {
        setLoadingParts(true);
        const result = await productService.getUserProducts({
          vehicleMake: resolvedCatalogVehicle.make,
          vehicleModel: resolvedCatalogVehicle.model,
          vehicleYear: resolvedCatalogVehicle.year,
          vehicleVariant: resolvedCatalogVehicle.variant,
          vehicleFuelType: resolvedCatalogVehicle.fuelType,
          limit: 20,
        });
        const products = result.products || result || [];
        if (Array.isArray(products) && products.length > 0) {
          setVehicleParts(products);
          return;
        }

        const selectedModelId = String(resolvedCatalogVehicle.modelId || "").trim();
        if (!selectedModelId) {
          setVehicleParts([]);
          return;
        }

        const modelCategories = await vehicleCatalogService.listCategoriesPublic(selectedModelId);
        const activeCategories = (Array.isArray(modelCategories) ? modelCategories : []).filter(
          (cat) => cat && cat.isActive !== false
        );
        if (activeCategories.length === 0) {
          setVehicleParts([]);
          return;
        }

        const partsBuckets = await Promise.all(
          activeCategories.map((cat) =>
            vehicleCatalogService.listPartsPublic(cat._id || cat.id, { modelId: selectedModelId })
          )
        );
        const flattened = partsBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
        setVehicleParts(flattened);
      } catch {
        setVehicleParts([]);
      } finally {
        setLoadingParts(false);
      }
    };
    fetchParts();
  }, [resolvedCatalogVehicle]);

  useEffect(() => {
    // Update showAll state when route changes
    setShowAll(isCategoryPage);
  }, [isCategoryPage]);

  const handleShowAll = useCallback(() => {
    setShowAll(true);
  }, []);

  // Group categories into chunks of 12 (3 rows x 4 columns) for mobile
  const mobileCategoriesChunks = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < categories.length; i += 12) {
      chunks.push(categories.slice(i, i + 12));
    }
    return chunks;
  }, []);

  // Mobile Swiper Config (3 rows x 4 columns = 12 items per slide, no autoplay)
  const mobileSwiperConfig = useMemo(() => ({
    modules: [],
    spaceBetween: 0,
    slidesPerView: 1,
    loop: false,
    speed: 400,
    allowTouchMove: true
  }), []);

  // Desktop Swiper Config
  const swiperConfig = useMemo(() => ({
    modules: [Autoplay, Navigation],
    spaceBetween: 12,
    slidesPerView: 3,
    loop: false,
    rewind: true,
    speed: 400,
    autoplay: {
      delay: 2000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    },
    navigation: {
      prevEl: '.category-prev',
      nextEl: '.category-next',
    },
    onSwiper: (swiper) => {
      swiperRef.current = swiper;
      if (swiper.autoplay) {
        swiper.autoplay.start();
      }
    },
    breakpoints: {
      640: {
        slidesPerView: 4,
        spaceBetween: 12
      },
      768: {
        slidesPerView: 4,
        spaceBetween: 14
      },
      1024: {
        slidesPerView: 5,
        spaceBetween: 16
      },
      1280: {
        slidesPerView: 6,
        spaceBetween: 16
      }
    }
  }), []);

  // Section state for catalogue
  const [section, setSection] = useState('new'); // 'new', 'aftermarket', 'used'

  // Filtered categories based on section (removed section switcher, showing all or reasonable default)
  const filteredCategories = useMemo(() => {
    return categories;
  }, [categories]);

  return (
    <section className={`relative overflow-hidden ${
      isDark
        ? `${isCategoryPage ? 'pt-20 md:pt-24 pb-8 md:pb-12' : 'py-6 sm:py-8 md:py-10'} bg-transparent`
        : isHomePage
          ? 'bg-slate-50 py-6 sm:py-8 md:py-10'
          : `bg-gray-50 ${isCategoryPage ? 'pt-20 md:pt-24 pb-8 md:pb-12' : 'py-6 sm:py-8 md:py-10'}`
    }`}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header Section */}
        <div className="mb-4 sm:mb-5 md:mb-6 flex flex-row items-center justify-between gap-3">
          <div className="flex-1">
            <h2 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
              {isCategoryPage ? 'All Categories' : 'Search by'}{' '}
              <span className={isDark ? 'text-sky-400' : 'text-blue-600'}>
                {isCategoryPage ? '' : 'Spares Catalogue'}
              </span>
            </h2>
            {resolvedCatalogVehicle && (resolvedCatalogVehicle.make || resolvedCatalogVehicle.model) && (
              <p className={`text-xs sm:text-sm font-semibold mt-1 flex items-center gap-1 ${isDark ? 'text-emerald-400/90' : 'text-green-700'}`}>
                <span>&#10003;</span>
                Showing parts for: {resolvedCatalogVehicle.make} {resolvedCatalogVehicle.model}
                {resolvedCatalogVehicle.year ? ` (${resolvedCatalogVehicle.year})` : ''}
                {resolvedCatalogVehicle.variant ? ` · ${resolvedCatalogVehicle.variant}` : ''}
              </p>
            )}
            {isCategoryPage && !selectedVehicle && (
              <p className="text-sm sm:text-base md:text-lg text-gray-600 font-medium mt-2">
                Browse through all available product categories
              </p>
            )}
          </div>
          {!showAll && !isCategoryPage && (
            <Link
              to="/category"
              className={`text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap flex-shrink-0 ${isDark ? 'text-amber-500 hover:text-amber-400' : 'text-blue-600 hover:underline'}`}
              aria-label="View All Categories"
            >
              VIEW ALL
            </Link>
          )}
        </div>


        {/* Categories Display - Grid for Mobile, Swiper for Desktop */}
        {loadingCategories ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 md:gap-5">
            {[...Array(6)].map((_, i) => (
              <CategoryCardSkeleton key={i} />
            ))}
          </div>
        ) : showAll || isCategoryPage ? (
          /* All Categories Grid - 4 columns mobile, 6 columns desktop */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 md:gap-5">
            {filteredCategories.map((cat, index) => (
              <CategoryCard
                key={cat.title}
                category={cat}
                index={index}
                isGrid={true}
                vehicleQuery={vehicleQuery}
              />
            ))}
          </div>
        ) : (
          <>
            {/* Mobile View: Horizontal Scrollable Swiper */}
            <div className="block md:hidden">
              <Swiper
                modules={[Autoplay]}
                spaceBetween={12}
                slidesPerView={3}
                loop={false}
                rewind={true}
                autoplay={{ delay: 2000, disableOnInteraction: false }}
                speed={400}
                className="w-full"
                breakpoints={{
                  320: { slidesPerView: 3, spaceBetween: 12 },
                  640: { slidesPerView: 4, spaceBetween: 12 },
                }}
              >
                {filteredCategories.map((cat, index) => (
                  <SwiperSlide key={cat.title}>
                    <CategoryCard
                      category={cat}
                      index={index}
                      isGrid={false}
                      vehicleQuery={vehicleQuery}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Desktop View: Swiper Container */}
            <div className="hidden md:block relative">
              <Swiper {...swiperConfig} className="category-swiper">
                {filteredCategories.map((cat, index) => (
                  <SwiperSlide key={cat.title}>
                    <CategoryCard
                      category={cat}
                      index={index}
                      isGrid={false}
                      vehicleQuery={vehicleQuery}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Navigation Arrows */}
              <button
                className={`category-prev absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg border ${isDark ? 'bg-slate-800/90 hover:bg-slate-700 border-slate-600' : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-gray-400'}`}
                aria-label="Previous slide"
                type="button"
              >
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-slate-200' : 'text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                className={`category-next absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg border ${isDark ? 'bg-slate-800/90 hover:bg-slate-700 border-slate-600' : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-gray-400'}`}
                aria-label="Next slide"
                type="button"
              >
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-slate-200' : 'text-gray-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

        {/* Vehicle Parts Section — dark cards (matches PDP / catalogue reference) */}
        {resolvedCatalogVehicle &&
          (resolvedCatalogVehicle.make || resolvedCatalogVehicle.model) &&
          vehicleParts.length > 0 && (
          <div className="mt-10 sm:mt-12">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h3 className={`text-[11px] sm:text-xs font-black uppercase tracking-[0.18em] max-w-[85%] transition-colors duration-300 ${isDark ? 'text-sky-400' : 'text-blue-600'}`}>
                Recommended for your {resolvedCatalogVehicle.make} {resolvedCatalogVehicle.model}
                {resolvedCatalogVehicle.year ? ` ${resolvedCatalogVehicle.year}` : ''}
              </h3>
              <Link
                to="/catalog"
                className={`text-sm font-bold shrink-0 inline-flex items-center gap-1 transition-colors duration-300 ${isDark ? 'text-amber-500 hover:text-amber-400' : 'text-blue-600 hover:text-blue-700'}`}
              >
                See all <span aria-hidden>→</span>
              </Link>
            </div>
            {loadingParts ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-3 gap-y-4 sm:gap-x-4 sm:gap-y-5">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-700/80 bg-[#1a1f26]/80 overflow-hidden animate-pulse"
                  >
                    <div className="h-20 bg-[#12161c]" />
                    <div className="p-2.5 space-y-1.5">
                      <div className="h-3 bg-slate-700 rounded w-1/3" />
                      <div className="h-4 bg-slate-700 rounded w-full" />
                      <div className="h-5 bg-slate-700 rounded w-1/2 mt-1" />
                      <div className="h-8 bg-slate-700 rounded-lg mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
                {vehicleParts.map((part, index) => (
                  <CatalogueDarkPartCard
                    key={part._id || part.id || index}
                    product={normalizeVehiclePart(part)}
                    index={index}
                    linkTarget="productRecord"
                    layout="grid"
                    vehicleQuery={vehicleQuery}
                    categoryData={{ name: typeof part.category === 'object' ? part.category?.name : part.category || 'Parts' }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

      {/* Custom Styles */}
      <style>{`
        .category-swiper .swiper-slide {
          height: auto;
        }
        .category-prev,
        .category-next {
          display: flex;
        }
        .category-prev.swiper-button-disabled,
        .category-next.swiper-button-disabled {
          opacity: 0.35;
          cursor: not-allowed;
          transform: scale(1);
        }
        .category-image {
          background: transparent;
        }
        .category-image:hover {
          filter: brightness(1.05);
        }
      `}</style>

    </section>
  );
}



